import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import nullable from '../../utils/nullable';

class EditableToggle extends Component {
  state = {
    mode: 'display',
    editValue: null,
    error: null,
  };
  inputRef = null;

  showEditMode = () => {
    const { mode } = this.state;
    const { value } = this.props;
    if (mode !== 'display') return;
    this.changeMode(
      {
        mode: 'edit',
        editValue: value,
        error: null,
      },
      () => {
        if (this.inputRef) {
          this.inputRef.focus();
          // Check if the control still has focus after 100ms.
          // If not, there is probably another editable control that doesn't validate and stole the focus again
          setTimeout(() => {
            if (this.inputRef !== document.activeElement) {
              this.handleCancel();
            }
          }, 100);
        }
      },
    );
  };
  registerInputRef = input => {
    this.inputRef = input;
  };

  handleValueChanged = e => this.handleSetValue(e.target.value);

  // Wrap setState into promise, so you can do something after the state has been set (eg: submit)
  handleSetValue = value =>
    new Promise(resolve =>
      this.setState(
        {
          editValue: value,
        },
        () => resolve(),
      ),
    );

  changeMode({ mode, ...rest }, callBack) {
    const { mode: prevMode } = this.state;
    const { onModeChange } = this.props;
    if (mode !== prevMode) {
      this.setState(
        {
          mode,
          ...rest,
        },
        () => {
          onModeChange(mode, prevMode);
          if (callBack) callBack();
        },
      );
    }
  }

  handleSubmit = () => {
    const { onSubmit, value, required } = this.props;
    const { editValue } = this.state;
    // Check if value changed, if not just cancel
    if (JSON.stringify(value) === JSON.stringify(editValue)) return this.handleCancel();
    if (!editValue && required) {
      if (this.inputRef) this.inputRef.focus();
      return;
    }
    this.changeMode(
      {
        mode: 'saving',
      },
      () => {
        Promise.resolve(onSubmit(editValue)).then(
          () => {
            // Success
            this.changeMode({
              mode: 'display',
            });
          },
          error => {
            this.changeMode(
              {
                mode: 'edit',
              },
              () => {
                if (this.inputRef) this.inputRef.focus();
              },
            );
          },
        );
      },
    );
  };
  handleCancel = () => {
    this.changeMode({
      mode: 'display',
    });
  };
  handleKeyDown = e => {
    // Escape
    if (e.keyCode === 27) {
      this.handleCancel();
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
    }
    // Enter
    else if (e.keyCode === 13) {
      this.handleSubmit();
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
    }
  };

  render() {
    const { children, displayValue, value, readOnly, className } = this.props;
    const { mode, editValue, error } = this.state;
    if (readOnly) return <div className={className}>{displayValue || value}</div>;

    if (mode === 'edit')
      return (
        <div className={classnames('react-editable react-editable-editing', className)}>
          <div>
            {children({
              // Handle the onChange of an input
              handleChange: this.handleValueChanged,
              // Set the input value directly, usefull for updating object values
              setInputValue: this.handleSetValue,
              // The current input value, bind this to the value attr of the input
              inputValue: editValue,
              // Put this in the ref attribute of the input, this will be used to set the focus on edit
              inputRef: this.registerInputRef,
              // When called, the current input value will be submitted to the parent, put this on the onBlur of the input
              submit: this.handleSubmit,
              // When called, the control will cancel and return to display mode
              cancel: this.handleCancel,
              // Handle submit on enter, cancel on escape. Put this on the onKeyDown of the input
              handleKeyDown: this.handleKeyDown,
            })}
          </div>
        </div>
      );
    if (mode === 'saving')
      return (
        <div className="react-editable react-editable-saving">
          <i className="fa fa-spinner fa-spin" />
        </div>
      );
    const events = readOnly ? {} : { onFocus: this.showEditMode, onClick: this.showEditMode };
    // Display mode
    return (
      <a
        tabIndex="0"
        role="tabpanel"
        className={classnames('react-editable react-editable-display', className)}
        {...events}
      >
        {displayValue || (value ? value.length === 0 ? <i className="glyphicon glyphicon-pencil" /> : value : value) || (
          <i className="glyphicon glyphicon-pencil" />
        )}
      </a>
    );
  }
}

EditableToggle.propTypes = {
  // Custom display value for when the control is in display mode
  displayValue: PropTypes.node,
  // Custom class for the display value
  className: PropTypes.string,
  // The current value
  value: nullable(
    PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.instanceOf(Date),
      PropTypes.arrayOf(PropTypes.shape({})),
      PropTypes.shape({}),
    ]),
  ).isRequired,
  // Handle value changes
  onSubmit: PropTypes.func.isRequired,
  // Notify when component changes mode
  onModeChange: PropTypes.func,
  // Disable edit mode
  readOnly: PropTypes.bool,

  // A render function to render the input, see function properties above
  children: PropTypes.func.isRequired,
  required: PropTypes.bool,
};

EditableToggle.defaultProps = {
  displayValue: null,
  readOnly: false,
  required: false,
  className: '',
  onModeChange: () => {},
};

export default EditableToggle;
